/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * OptimizerView — Resource Allocation Optimizer (Tier 1.3).
 *
 * Three main sections:
 *   1. Planner card: form to generate an optimized plan
 *   2. Plan results: ranked turfs table + confirm button (Phase A)
 *   3. Coverage: per-precinct statistics (live fetch on mount)
 *   4. Recent plans: plan history (Phase B stub)
 *
 * All endpoints are role-gated to COORDINATOR_PLUS_ROLES.
 * 401 → auth interceptor handles. 403 → show banner.
 * 501 (confirm) → toast "Phase B coming soon".
 */
import React, { useEffect, useState } from 'react';
import {
  generatePlan,
  getCoverage,
  confirmPlan,
  getPlanHistory,
  formatOptimizerError,
  type PlanRequest,
  type PlanResponse,
  type CoverageResponse,
  type PlanHistoryResponse,
  type TurfCandidate,
} from '@/api/optimizer';
import type { PlanConfirmRequest } from '@/api/optimizer';
import { ViewHeader } from '@/components/ViewHeader';

// ============================================================================
// Helper Components
// ============================================================================

function ErrorBanner({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss?: () => void;
}) {
  return (
    <div className="rounded border border-status-danger bg-status-danger-bg/40 px-4 py-3 text-status-danger flex items-center justify-between mb-4">
      <span>{message}</span>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="text-status-danger hover:text-fg-primary font-semibold"
        >
          ×
        </button>
      )}
    </div>
  );
}

function SuccessBanner({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss?: () => void;
}) {
  return (
    <div className="rounded border border-status-success bg-status-success-bg/40 px-4 py-3 text-status-success flex items-center justify-between mb-4">
      <span>{message}</span>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="text-status-success hover:text-fg-primary font-semibold"
        >
          ×
        </button>
      )}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-8">
      <div className="inline-flex items-center gap-2">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-500 border-t-red-500" />
        <span className="text-fg-secondary">Loading…</span>
      </div>
    </div>
  );
}

function TierChip({
  tier,
  count,
}: {
  tier: string;
  count: number;
}) {
  const colors: Record<string, string> = {
    anchor: 'bg-tier-anchor text-fg-primary',
    hot: 'bg-tier-hot text-fg-primary',
    warm: 'bg-tier-warm text-fg-primary',
    cool: 'bg-tier-cool text-fg-primary',
    super_voter: 'bg-tier-warm text-fg-primary',
  };
  const cls = colors[tier] || 'bg-tier-cool text-fg-primary';
  return (
    <span className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold ${cls}`}>
      {tier}
      <span className="ml-1 font-mono text-[10px]">{count}</span>
    </span>
  );
}

// ============================================================================
// Main View
// ============================================================================

export default function OptimizerView({
  onBack,
}: {
  onBack: () => void;
}) {
  // ─────────────────────────────────────────────────────────────────────────
  // Planner Form State
  // ─────────────────────────────────────────────────────────────────────────
  const [electionDate, setElectionDate] = useState('2026-08-04');
  const [electionType, setElectionType] = useState('primary');
  const [availableShifts, setAvailableShifts] = useState(10);
  const [preferAnchorHeavy, setPreferAnchorHeavy] = useState(false);
  const [excludedPrecincts, setExcludedPrecincts] = useState('');

  // ─────────────────────────────────────────────────────────────────────────
  // Plan Results State
  // ─────────────────────────────────────────────────────────────────────────
  const [planResponse, setPlanResponse] = useState<PlanResponse | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [turfSortKey, setTurfSortKey] = useState<'priority_score' | 'door_count' | 'expected_ballots_moved'>('priority_score');
  const [turfSortDesc, setTurfSortDesc] = useState(true);

  // Canvasser assignments for each turf (route_id -> canvasser_id)
  const [turfAssignments, setTurfAssignments] = useState<Record<number, number>>({});

  // ─────────────────────────────────────────────────────────────────────────
  // Coverage State
  // ─────────────────────────────────────────────────────────────────────────
  const [coverageData, setCoverageData] = useState<CoverageResponse | null>(null);
  const [coverageLoading, setCoverageLoading] = useState(true);
  const [coverageError, setCoverageError] = useState<string | null>(null);

  // ─────────────────────────────────────────────────────────────────────────
  // History State
  // ─────────────────────────────────────────────────────────────────────────
  const [historyData, setHistoryData] = useState<PlanHistoryResponse | null>(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // ─────────────────────────────────────────────────────────────────────────
  // Misc State
  // ─────────────────────────────────────────────────────────────────────────
  const [roleError, setRoleError] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────
  // Fetch coverage and history on mount
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        setCoverageLoading(true);
        setCoverageError(null);
        const data = await getCoverage();
        setCoverageData(data);
      } catch (err: any) {
        if (err?.response?.status === 403) {
          setRoleError(true);
        } else {
          setCoverageError(formatOptimizerError(err));
        }
      } finally {
        setCoverageLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setHistoryLoading(true);
        setHistoryError(null);
        const data = await getPlanHistory(10);
        setHistoryData(data);
      } catch (err: any) {
        if (err?.response?.status === 403) {
          setRoleError(true);
        } else {
          setHistoryError(formatOptimizerError(err));
        }
      } finally {
        setHistoryLoading(false);
      }
    })();
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Generate plan handler
  // ─────────────────────────────────────────────────────────────────────────
  async function handleGeneratePlan(e: React.FormEvent) {
    e.preventDefault();
    try {
      setPlanLoading(true);
      setPlanError(null);
      setSuccessMessage(null);

      const excluded = excludedPrecincts
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);

      const request: PlanRequest = {
        election_date: electionDate,
        election_type: electionType,
        available_shifts: availableShifts,
        prefer_anchor_heavy: preferAnchorHeavy,
        excluded_precincts: excluded.length > 0 ? excluded : undefined,
      };

      const response = await generatePlan(request);
      setPlanResponse(response);
      setTurfAssignments({});
      setSuccessMessage(`Generated ${response.turfs.length} turf candidates.`);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setRoleError(true);
      } else {
        setPlanError(formatOptimizerError(err));
      }
    } finally {
      setPlanLoading(false);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Sort and slice turfs for display
  // ─────────────────────────────────────────────────────────────────────────
  const sortedTurfs = (() => {
    if (!planResponse?.turfs) return [];
    const sorted = [...planResponse.turfs].sort((a, b) => {
      let aVal: any = a[turfSortKey];
      let bVal: any = b[turfSortKey];
      if (aVal === null || aVal === undefined) aVal = 0;
      if (bVal === null || bVal === undefined) bVal = 0;
      const cmp = aVal - bVal;
      return turfSortDesc ? -cmp : cmp;
    });
    return sorted.slice(0, availableShifts);
  })();

  // ─────────────────────────────────────────────────────────────────────────
  // Confirm plan handler
  // ─────────────────────────────────────────────────────────────────────────
  async function handleConfirmPlan() {
    if (!planResponse) return;
    try {
      setConfirmLoading(true);
      setPlanError(null);

      const turfs = sortedTurfs.map(turf => ({
        route_id: turf.route_id,
        canvasser_id: turfAssignments[turf.route_id] || 0,
        predicted_votes_moved: turf.expected_ballots_moved,
        planned_start_at: null,
      }));

      const request: PlanConfirmRequest = {
        election_date: planResponse.election_date,
        election_type: planResponse.election_type,
        turfs,
        notes: null,
      };

      await confirmPlan(request);
      setSuccessMessage('Plan confirmed and shifts created!');
      setPlanResponse(null);
      setTurfAssignments({});
    } catch (err: any) {
      if (err?.response?.status === 501) {
        setSuccessMessage('Phase B — coming soon. Plan persistence not yet available.');
      } else if (err?.response?.status === 403) {
        setRoleError(true);
      } else {
        setPlanError(formatOptimizerError(err));
      }
    } finally {
      setConfirmLoading(false);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <ViewHeader
        title="Resource Allocation Optimizer"
        tagline="Generate optimized canvass plans to maximize voter contact and ballot movement."
      />

      {/* Role Error Banner */}
      {roleError && (
        <ErrorBanner
          message="You need coordinator access to use the optimizer."
          onDismiss={() => setRoleError(false)}
        />
      )}

      {/* Messages */}
      {successMessage && (
        <SuccessBanner
          message={successMessage}
          onDismiss={() => setSuccessMessage(null)}
        />
      )}
      {planError && (
        <ErrorBanner
          message={planError}
          onDismiss={() => setPlanError(null)}
        />
      )}

      {/* Planner Card */}
      <div className="rounded-lg border border-border-base bg-bg-raised/50 p-6">
        <h2 className="text-xl font-bold text-fg-primary mb-4">Plan Generator</h2>
        <form onSubmit={handleGeneratePlan} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Election Date */}
            <div>
              <label htmlFor="optimizer-election-date" className="block text-sm font-semibold text-fg-secondary mb-1">
                Election Date
              </label>
              <input
                id="optimizer-election-date"
                type="date"
                value={electionDate}
                onChange={e => setElectionDate(e.target.value)}
                className="w-full rounded border border-border-subtle bg-bg-elevated text-fg-primary px-3 py-2 text-sm"
              />
            </div>

            {/* Election Type */}
            <div>
              <label htmlFor="optimizer-election-type" className="block text-sm font-semibold text-fg-secondary mb-1">
                Election Type
              </label>
              <select
                id="optimizer-election-type"
                value={electionType}
                onChange={e => setElectionType(e.target.value)}
                className="w-full rounded border border-border-subtle bg-bg-elevated text-fg-primary px-3 py-2 text-sm"
              >
                <option value="primary">Primary</option>
                <option value="general">General</option>
              </select>
            </div>

            {/* Available Shifts */}
            <div>
              <label htmlFor="optimizer-available-shifts" className="block text-sm font-semibold text-fg-secondary mb-1">
                Available Shifts
              </label>
              <input
                id="optimizer-available-shifts"
                type="number"
                min={1}
                max={500}
                value={availableShifts}
                onChange={e => setAvailableShifts(Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))}
                className="w-full rounded border border-border-subtle bg-bg-elevated text-fg-primary px-3 py-2 text-sm"
              />
            </div>

            {/* Prefer Anchor Heavy */}
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-fg-secondary cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferAnchorHeavy}
                  onChange={e => setPreferAnchorHeavy(e.target.checked)}
                  className="w-4 h-4 rounded border-border-subtle bg-bg-elevated text-red-600 min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"
                />
                <span className="text-sm font-semibold">Prefer Anchor-Heavy Routes</span>
              </label>
            </div>
          </div>

          {/* Excluded Precincts */}
          <div>
            <label className="block text-sm font-semibold text-fg-secondary mb-1">
              Excluded Precincts (comma-separated)
            </label>
            <input
              type="text"
              value={excludedPrecincts}
              onChange={e => setExcludedPrecincts(e.target.value)}
              placeholder="e.g., 001, 002, 003"
              className="w-full rounded border border-border-subtle bg-bg-elevated text-fg-primary px-3 py-2 text-sm"
            />
          </div>

          {/* Generate Button */}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={planLoading}
              className="px-4 py-2 rounded bg-accent hover:bg-accent-hover disabled:bg-bg-elevated text-white font-semibold text-sm transition min-h-11 sm:min-h-0"
            >
              {planLoading ? 'Generating…' : 'Generate Plan'}
            </button>
          </div>
        </form>
      </div>

      {/* Plan Results */}
      {planLoading && <LoadingSpinner />}

      {planResponse && (
        <div className="space-y-4">
          {/* Summary Strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg border border-border-base bg-bg-raised/50 p-4">
              <div className="text-xs font-semibold text-fg-muted uppercase tracking-wide">
                Total Turfs
              </div>
              <div className="text-2xl font-bold text-fg-primary mt-1">
                {planResponse.turfs.length}
              </div>
            </div>
            <div className="rounded-lg border border-border-base bg-bg-raised/50 p-4">
              <div className="text-xs font-semibold text-fg-muted uppercase tracking-wide">
                Total Doors
              </div>
              <div className="text-2xl font-bold text-fg-primary mt-1">
                {planResponse.total_doors}
              </div>
            </div>
            <div className="rounded-lg border border-border-base bg-bg-raised/50 p-4">
              <div className="text-xs font-semibold text-fg-muted uppercase tracking-wide">
                Expected Ballots Moved
              </div>
              <div className="text-2xl font-bold text-accent-fg mt-1">
                {Math.round(planResponse.total_expected_ballots_moved)}
              </div>
            </div>
            <div className="rounded-lg border border-border-base bg-bg-raised/50 p-4">
              <div className="text-xs font-semibold text-fg-muted uppercase tracking-wide">
                Contact Rate
              </div>
              <div className="text-2xl font-bold text-status-warning mt-1">
                {(planResponse.assumed_contact_rate * 100).toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Turfs Table */}
          <div className="rounded-lg border border-border-base bg-bg-raised/50 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-base bg-bg-base/50">
                    <th className="px-4 py-3 text-left font-semibold text-fg-secondary">
                      Route Name
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-fg-secondary">
                      Precinct
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-fg-secondary">
                      Doors
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-fg-secondary">
                      Voters
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-fg-secondary">
                      Tiers
                    </th>
                    <th
                      className="px-4 py-3 text-center font-semibold text-fg-secondary cursor-pointer hover:text-fg-primary"
                      onClick={() => {
                        if (turfSortKey === 'expected_ballots_moved') {
                          setTurfSortDesc(!turfSortDesc);
                        } else {
                          setTurfSortKey('expected_ballots_moved');
                          setTurfSortDesc(true);
                        }
                      }}
                    >
                      Est. Ballots
                    </th>
                    <th
                      className="px-4 py-3 text-center font-semibold text-fg-secondary cursor-pointer hover:text-fg-primary"
                      onClick={() => {
                        if (turfSortKey === 'priority_score') {
                          setTurfSortDesc(!turfSortDesc);
                        } else {
                          setTurfSortKey('priority_score');
                          setTurfSortDesc(true);
                        }
                      }}
                    >
                      Priority Score
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-fg-secondary">
                      Under-Contacted
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-fg-secondary">
                      Assign Canvasser
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTurfs.map((turf, idx) => (
                    <tr
                      key={turf.route_id}
                      className={`border-b border-border-base ${
                        idx % 2 === 0 ? 'bg-bg-raised/25' : ''
                      } hover:bg-bg-elevated/30 transition`}
                    >
                      <td className="px-4 py-3 text-fg-primary font-semibold">
                        {turf.route_name}
                      </td>
                      <td className="px-4 py-3 text-fg-secondary">
                        {turf.precinct_num || '—'}
                      </td>
                      <td className="px-4 py-3 text-center text-fg-secondary">
                        {turf.door_count}
                      </td>
                      <td className="px-4 py-3 text-center text-fg-secondary">
                        {turf.voter_count}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(turf.tier_counts).map(
                            ([tier, count]) =>
                              count > 0 && (
                                <TierChip key={tier} tier={tier} count={count} />
                              )
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-accent-fg font-semibold">
                        {Math.round(turf.expected_ballots_moved)}
                      </td>
                      <td className="px-4 py-3 text-center font-mono text-fg-secondary">
                        {turf.priority_score.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {turf.under_contacted ? (
                          <span className="inline-flex items-center gap-1 rounded bg-status-warning-bg/20 text-status-warning border border-status-warning/50 px-2 py-1 text-xs font-semibold">
                            Yes
                          </span>
                        ) : (
                          <span className="text-fg-muted">No</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min={0}
                          value={turfAssignments[turf.route_id] || ''}
                          onChange={e => {
                            const val = parseInt(e.target.value);
                            if (e.target.value === '') {
                              setTurfAssignments(prev => {
                                const next = { ...prev };
                                delete next[turf.route_id];
                                return next;
                              });
                            } else if (!isNaN(val)) {
                              setTurfAssignments(prev => ({
                                ...prev,
                                [turf.route_id]: val,
                              }));
                            }
                          }}
                          placeholder="ID"
                          className="w-16 rounded border border-border-subtle bg-bg-elevated text-fg-primary px-2 py-1 text-sm"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Confirm Plan Button */}
          <div className="flex gap-2">
            <button
              onClick={handleConfirmPlan}
              disabled={confirmLoading}
              className="px-4 py-2 rounded bg-status-success-bg hover:bg-status-success-bg/80 disabled:bg-bg-elevated text-white font-semibold text-sm transition"
            >
              {confirmLoading ? 'Confirming…' : 'Confirm Plan'}
            </button>
          </div>
        </div>
      )}

      {/* Coverage Card */}
      {coverageLoading ? (
        <LoadingSpinner />
      ) : coverageError ? (
        <ErrorBanner
          message={coverageError}
          onDismiss={() => setCoverageError(null)}
        />
      ) : coverageData ? (
        <div className="rounded-lg border border-border-base bg-bg-raised/50 p-6">
          <h2 className="text-xl font-bold text-fg-primary mb-4">
            Coverage by Precinct
          </h2>
          <div className="mb-4 p-3 bg-bg-base/50 rounded border border-border-base">
            <div className="text-xs font-semibold text-fg-muted uppercase tracking-wide">
              Tenant Average Contact Rate
            </div>
            <div className="text-2xl font-bold text-status-warning mt-1">
              {(coverageData.tenant_avg_contact_rate * 100).toFixed(1)}%
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-base bg-bg-base/50">
                  <th className="px-4 py-3 text-left font-semibold text-fg-secondary">
                    Precinct
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-fg-secondary">
                    Voters
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-fg-secondary">
                    Contacted
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-fg-secondary">
                    Contact Rate
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-fg-secondary">
                    Exp. Ballots
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-fg-secondary">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {coverageData.precincts.map((row, idx) => (
                  <tr
                    key={row.precinct_num}
                    className={`border-b border-border-base ${
                      row.under_contacted
                        ? 'bg-status-warning-bg/25'
                        : idx % 2 === 0
                        ? 'bg-bg-raised/25'
                        : ''
                    } hover:bg-bg-elevated/30 transition`}
                  >
                    <td className="px-4 py-3 text-fg-primary font-semibold">
                      {row.precinct_num}
                    </td>
                    <td className="px-4 py-3 text-center text-fg-secondary">
                      {row.voter_count}
                    </td>
                    <td className="px-4 py-3 text-center text-fg-secondary">
                      {row.contacted_count}
                    </td>
                    <td className="px-4 py-3 text-center font-semibold text-status-warning">
                      {(row.contact_rate * 100).toFixed(1)}%
                    </td>
                    <td className="px-4 py-3 text-center text-accent-fg font-semibold">
                      {Math.round(row.expected_ballots)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row.under_contacted ? (
                        <span className="inline-flex items-center gap-1 rounded bg-status-warning-bg/20 text-status-warning border border-status-warning/50 px-2 py-1 text-xs font-semibold">
                          Under-contacted
                        </span>
                      ) : (
                        <span className="text-fg-muted text-xs">On track</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Recent Plans Card */}
      {historyLoading ? (
        <LoadingSpinner />
      ) : historyError ? (
        <ErrorBanner
          message={historyError}
          onDismiss={() => setHistoryError(null)}
        />
      ) : historyData ? (
        <div className="rounded-lg border border-border-base bg-bg-raised/50 p-6">
          <h2 className="text-xl font-bold text-fg-primary mb-4">Recent Plans</h2>

          {historyData.plans.length === 0 ? (
            <div className="text-center py-8 text-fg-muted">
              <p className="text-sm">No saved plans yet.</p>
              <p className="text-xs text-fg-muted mt-1">
                Confirm a plan to build history.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-base bg-bg-base/50">
                    <th className="px-4 py-3 text-left font-semibold text-fg-secondary">
                      Plan ID
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-fg-secondary">
                      Planned Start
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-fg-secondary">
                      Shifts
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-fg-secondary">
                      Est. Ballots Moved
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-fg-secondary">
                      Routes
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-fg-secondary">
                      Canvassers
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {historyData.plans.map((plan, idx) => (
                    <tr
                      key={plan.plan_id}
                      className={`border-b border-border-base ${
                        idx % 2 === 0 ? 'bg-bg-raised/25' : ''
                      } hover:bg-bg-elevated/30 transition`}
                    >
                      <td className="px-4 py-3 text-fg-primary font-mono text-xs">
                        {plan.plan_id}
                      </td>
                      <td className="px-4 py-3 text-fg-secondary">
                        {new Date(plan.planned_start_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-center text-fg-secondary">
                        {plan.shift_count}
                      </td>
                      <td className="px-4 py-3 text-center text-accent-fg font-semibold">
                        {Math.round(plan.total_predicted_votes_moved)}
                      </td>
                      <td className="px-4 py-3 text-center text-fg-secondary">
                        {plan.routes_covered}
                      </td>
                      <td className="px-4 py-3 text-center text-fg-secondary">
                        {plan.canvassers_assigned}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
